import React from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, Award, TrendingUp, Users } from 'lucide-react';
import { tradeHistory, clients } from '../mock';

export const TradeHistory = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-emerald-600 text-white text-base px-4 py-1">Our Track Record</Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Trade History & Clients</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A proven track record of excellence and trusted partnerships across Southern Africa
          </p>
        </div>
      </section>

      {/* Stats Overview */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardHeader className="text-center">
                <TrendingUp className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
                <CardTitle className="text-4xl text-emerald-600">R25M+</CardTitle>
                <p className="text-gray-600">First Contract Value</p>
              </CardHeader>
            </Card>
            
            <Card className="bg-teal-50 border-teal-200">
              <CardHeader className="text-center">
                <Award className="h-12 w-12 text-teal-600 mx-auto mb-3" />
                <CardTitle className="text-4xl text-teal-600">7+</CardTitle>
                <p className="text-gray-600">Years of Excellence</p>
              </CardHeader>
            </Card>
            
            <Card className="bg-cyan-50 border-cyan-200">
              <CardHeader className="text-center">
                <Users className="h-12 w-12 text-cyan-600 mx-auto mb-3" />
                <CardTitle className="text-4xl text-cyan-600">22+</CardTitle>
                <p className="text-gray-600">Major Contracts</p>
              </CardHeader>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="text-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <CardTitle className="text-4xl text-green-600">100%</CardTitle>
                <p className="text-gray-600">Client Satisfaction</p>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-teal-100 text-teal-700 text-base px-4 py-1">Our Journey</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Growth Timeline</h2>
            <p className="text-xl text-gray-600">
              From humble beginnings to regional leader
            </p>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-emerald-200"></div>
            
            <div className="space-y-12">
              {tradeHistory.map((milestone, index) => (
                <div key={index} className="relative pl-20">
                  {/* Circle marker */}
                  <div className="absolute left-5 top-2 h-8 w-8 rounded-full bg-emerald-600 border-4 border-white shadow-lg flex items-center justify-center">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  </div>
                  
                  <Card className="border-2 border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all duration-300">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <Badge className="mb-3 bg-emerald-100 text-emerald-700">{milestone.year}</Badge>
                          <CardTitle className="text-2xl mb-2">{milestone.title}</CardTitle>
                          <p className="text-gray-600 text-base">{milestone.description}</p>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 text-base px-4 py-1">Client Success</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Trusted by Industry Leaders</h2>
            <p className="text-xl text-gray-600">
              What our clients say about working with us
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {clients.map((client) => (
              <Card key={client.id} className="border-2 border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Users className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{client.name}</CardTitle>
                      <Badge variant="secondary" className="mt-1 bg-teal-100 text-teal-700">
                        {client.industry}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 italic">"{client.testimonial}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Credentials</h2>
            <p className="text-xl text-emerald-50">
              Certified, licensed, and trusted
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Award className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold mb-2">Level 1 B-BBEE</h3>
              <p className="text-emerald-100">Certified empowerment credentials</p>
            </div>
            
            <div className="text-center p-6">
              <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold mb-2">Licensed Wholesaler</h3>
              <p className="text-emerald-100">Valid certificate from Department of Energy</p>
            </div>
            
            <div className="text-center p-6">
              <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold mb-2">Direct Loading</h3>
              <p className="text-emerald-100">Authorized by Shell, Engen & BP</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TradeHistory;